import { openNewChatComposer } from '@/lib/openHomeComposer';
import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import ChatDropdownView from './ChatDropdownView';
import { useRouter } from 'expo-router';
import { useCollectionStore, useAuthStore, useToastStore, useModalStore, useChatStore, useUIStore, useDeckStore } from '@/store';
import { displayChatTitle } from '@/lib/chatTitle';
import { useChat } from '@/hooks/useChat';
import useRequireAuth from '@/hooks/useRequireAuth';
import useDeckAvailability from '@/hooks/useDeckAvailability';
import { useTranslation } from '@/hooks/useTranslation';


function ChatDropdown(props) {
  const { conversationId, withDeck, withWorkTasks } = props;
  const { t } = useTranslation();
  const router = useRouter();
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const { addToast } = useToastStore(state => state.actions);
  const { openModal } = useModalStore(state => state.actions);
  const requireAuth = useRequireAuth();

  const [isCreatingDeck, setIsCreatingDeck] = useState(false);
  const creatingDeckRef = useRef(false);
  const deckRequestRef = useRef(0);
  useEffect(() => {
    creatingDeckRef.current = false;
    setIsCreatingDeck(false);
    return () => { deckRequestRef.current += 1; };
  }, [conversationId]);

  // Multi-select state
  const isSelectingMultipleChats = useChatStore(state => state.isSelectingMultipleChats);
  const toggleSelectingMultipleChats = useChatStore(state => state.toggleSelectingMultipleChats);
  const startSelectingMultipleChats = useChatStore(state => state.startSelectingMultipleChats);
  const toggleSelectChatForMulti = useChatStore(state => state.toggleSelectChatForMulti);

  const handleStartSelecting = useCallback(() => {
    if (isSelectingMultipleChats) {
      toggleSelectingMultipleChats();
    } else {
      startSelectingMultipleChats();
      toggleSelectChatForMulti(conversationId);
    }
  }, [isSelectingMultipleChats, toggleSelectingMultipleChats, startSelectingMultipleChats, toggleSelectChatForMulti, conversationId]);

  // Get the raw collections data
  const collectionsMap = useCollectionStore(state => state.collections);
  const collectionIds = useCollectionStore(state => state.collectionIds);
  const addConversationToCollection = useCollectionStore(state => state.addConversationToCollection);
  const removeConversationFromCollection = useCollectionStore(state => state.removeConversationFromCollection);

  // Memoize the collections array
  const collections = useMemo(() => {
    return collectionIds.map((id) => collectionsMap[id]);
  }, [collectionsMap, collectionIds]);

  const handleCreateNewCollection = useCallback(() => {
    requireAuth(
      () => openModal('CreateCollectionModal'),
      {
        source: 'chat_dropdown_create_collection',
        title: 'Sign in to create a Collection',
        subtitle: 'Collections group files and chats so Gab can pull from the same context across every conversation.',
        icon: 'folder-plus',
      },
    );
  }, [openModal, requireAuth]);
  const requestOpenConversationFind = useUIStore(state => state.requestOpenConversationFind);
  const handleFindInChat = useCallback(() => {
    requestOpenConversationFind(conversationId);
  }, [conversationId, requestOpenConversationFind]);

  // Destructure all needed values from useChat hook
  const { 
    chat,
    isTemporary,
    canShare,
    isPinned, 
    isArchiving, 
    isDeleting, 
    isArchived,
    handleArchiveConfirm, 
    handleDeleteConfirm, 
    handleShare, 
    handleTogglePinChat, 
    handleRename,
    handleExportChat,
    handleSaveTemporaryConfirm,
    handleOpenTemporarySettings,
  } = useChat(conversationId);

  const inCollectionId = useMemo(() => chat?.inCollectionId, [chat?.inCollectionId]);
  const isDesktop = useDeckAvailability();
  const canOpenDeck = withDeck && isDesktop && chat?.isOwner && conversationId && !isTemporary && !isGhost;
  const handleOpenDeck = useCallback(async () => {
    if (!canOpenDeck || creatingDeckRef.current) return;
    creatingDeckRef.current = true;
    setIsCreatingDeck(true);
    const request = ++deckRequestRef.current;
    const actions = useDeckStore.getState().actions;
    try {
      const title = displayChatTitle(chat?.title);
      const slug = await actions.createDeck({
        name: title,
        makeActive: false,
        columns: [
          { type: 'conversation', ref: { kind: 'Conversation', id: conversationId }, label: title },
          { type: 'conversation', ref: { kind: 'Conversation', id: null } },
        ],
      });
      if (slug && request === deckRequestRef.current) {
        actions.setActiveDeckSlug(slug);
        router.push({ pathname: '/deck', params: { d: slug } });
      }
    } finally {
      if (request === deckRequestRef.current) {
        creatingDeckRef.current = false;
        setIsCreatingDeck(false);
      }
    }
  }, [canOpenDeck, chat?.title, conversationId, router]);
  const canManageWorkTasks = withWorkTasks && chat?.isOwner && !isTemporary && !isGhost;
  const handleManageWorkTasks = useCallback(() => {
    openModal('WorkChatTasksModal', { conversationId, chatTitle: chat?.title });
  }, [openModal, conversationId, chat?.title]);

  const handleReport = useCallback(() => {
    openModal('ReportModal', {
      isOpen: true,
      conversationId,
    });
  }, [openModal, conversationId]);

  const handleAddToCollection = useCallback(async (collectionId) => {
    try {
      if (collectionId === null && inCollectionId) {
        await removeConversationFromCollection(inCollectionId, conversationId);
        addToast(t('dropdowns.chatRemovedFromCollection'), 'success');
      } else {
        await addConversationToCollection(collectionId, conversationId);
        addToast(t('dropdowns.chatAddedToCollection'), 'success', [
          {
            title: t('dropdowns.viewCollection'),
            href: `/collections/${collectionId}`,
          }
        ]);
      }
    } catch (error) {
      console.error('Error managing chat collection:', error);
    }
  }, [conversationId, inCollectionId, addConversationToCollection, removeConversationFromCollection, addToast, t]);

  return <ChatDropdownView {...props} controller={{
    chat, isTemporary, canShare, isPinned, isArchiving, isDeleting, isArchived, isGhost,
    handleArchiveConfirm, handleDeleteConfirm, handleShare, handleTogglePinChat, handleRename, handleExportChat,
    handleSaveTemporaryConfirm, handleOpenTemporarySettings, isSelectingMultipleChats, handleStartSelecting,
    collections, inCollectionId, handleCreateNewCollection, handleFindInChat, canOpenDeck, isCreatingDeck,
    handleOpenDeck, canManageWorkTasks, handleManageWorkTasks, handleReport, handleAddToCollection,
    handleNewChat: () => openNewChatComposer(router),
    handleDetails: () => openModal('ChatDetailsModal', { conversationId }),
  }} />;
}

export default React.memo(ChatDropdown);
